/**
 * Session: the signed-in user from `GET /v1/me`, the active org, and the Actor that `useCan`
 * evaluates with the SAME `authorize()` the API runs (docs/03 "Enforcement points"). A hidden
 * item is a denied route because both sides decide on the same data.
 */
import {
  entitlementSnapshotFromJSON,
  type Actor,
  type EntitlementSnapshotJSON,
  type OrgRole,
  type Permission,
  type PlatformRole,
  type TrustBand,
  type VerificationLevel,
} from '@oex/auth';
import { useIdentityGetMe, type MeDto } from '@oex/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { orgStore, tokenStore } from './api';

export type Me = MeDto;
export type MeMembership = MeDto['memberships'][number];

export type Session = {
  status: 'anonymous' | 'loading' | 'ready' | 'error';
  me: Me | null;
  /** Active memberships usable in the portal (not locked by suspension). */
  memberships: MeMembership[];
  activeOrgId: string | null;
  activeMembership: MeMembership | null;
  actor: Actor | null;
  isPlatform: boolean;
  switchOrg(orgId: string): void;
  refresh(): Promise<unknown>;
};

/** Exported for tests, which provide a fixed session instead of fetching `/v1/me`. */
export const SessionContext = createContext<Session | null>(null);

/** Build the Actor for the active org from `/v1/me` (docs/03 "The actor"). */
export function actorFromMe(me: Me, orgId: string | null): Actor {
  const m = orgId ? me.memberships.find((x) => x.org.id === orgId) : undefined;
  return {
    userId: me.user.id,
    ...(me.platformRole && !me.impersonation ? { platformRole: me.platformRole as PlatformRole } : {}),
    ...(me.impersonation ? { impersonatedBy: me.impersonation.staffUserId } : {}),
    ...(m
      ? {
          org: {
            orgId: m.org.id,
            role: m.role as OrgRole,
            permissions: new Set(m.permissions as Permission[]),
            entitlements: entitlementSnapshotFromJSON(m.entitlements as EntitlementSnapshotJSON),
            trustBand: m.trustBand as TrustBand,
            verificationLevel: m.verificationLevel as VerificationLevel,
            ...(m.locked ? { membershipStatus: 'suspended' as const } : {}),
          },
        }
      : {}),
  };
}

export function pickActiveOrg(me: Me, stored: string | null): string | null {
  const usable = me.memberships.filter((m) => !m.locked);
  if (stored && usable.some((m) => m.org.id === stored)) return stored;
  if (me.user.lastOrgId && usable.some((m) => m.org.id === me.user.lastOrgId)) return me.user.lastOrgId;
  return usable[0]?.org.id ?? null;
}

export function SessionProvider({ children, token }: { children: ReactNode; token: string | null }) {
  const queryClient = useQueryClient();
  const [storedOrg, setStoredOrg] = useState<string | null>(() => orgStore.get());
  const meQuery = useIdentityGetMe({
    query: { queryKey: ['identity', 'me', token], enabled: Boolean(token), staleTime: 60_000 },
  });
  const me = meQuery.data ?? null;
  const activeOrgId = me ? pickActiveOrg(me, storedOrg) : null;
  const switchOrg = useCallback(
    (orgId: string) => {
      orgStore.set(orgId);
      setStoredOrg(orgId);
      // Query keys carry the org, but a switch must never show the previous org's data.
      queryClient.removeQueries({
        predicate: (q) => !(q.queryKey[0] === 'identity' && q.queryKey[1] === 'me'),
      });
    },
    [queryClient],
  );
  const value = useMemo<Session>(() => {
    const status: Session['status'] = !token
      ? 'anonymous'
      : meQuery.isError
        ? 'error'
        : me
          ? 'ready'
          : 'loading';
    return {
      status,
      me,
      memberships: me ? me.memberships.filter((m) => !m.locked) : [],
      activeOrgId,
      activeMembership: me?.memberships.find((m) => m.org.id === activeOrgId) ?? null,
      actor: me ? actorFromMe(me, activeOrgId) : null,
      isPlatform: Boolean(me?.platformRole) && !me?.impersonation,
      switchOrg,
      refresh: () => meQuery.refetch(),
    };
  }, [token, me, meQuery, activeOrgId, switchOrg]);
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): Session {
  const s = useContext(SessionContext);
  if (!s) throw new Error('useSession outside <SessionProvider>');
  return s;
}

/** The active org id for API calls outside React (configureApi). */
export function currentOrgIdFrom(me: Me | null): string | null {
  return me ? pickActiveOrg(me, orgStore.get()) : null;
}

export { tokenStore };
